import { Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { forkJoin, Observable, Subject } from 'rxjs';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ActionsSubject, Store } from '@ngrx/store';
import { ofType } from '@ngrx/effects';
import { PtCoreActions, PtCoreSelectors } from '@pt/pt-core';
import { takeUntil } from 'rxjs/operators';
import { AiAssessmentActions, ValidatorGenerator } from '@pt/pt-shared';
import {
  NavigationData,
  PtUiAppRoutes,
  REQUIRED_VALIDATOR_KEY,
} from '@pt/pt-models';
import { AiAssessmentsService, FolderService } from '@pt/api-client';
import { DuplicateAiAssessmentPayload } from '../../../models';
import { AiAssessmentDocCreateRequest } from '@primatoo-ms-organization';
import { Folder } from '@ms-organization-entities';
import { ListAppForNewAssessmentResponse } from 'it/primatoo/openapi/api/org/schemas/appassessment/ListAppForNewAssessmentResponse';
import { Organization } from 'it/primatoo/openapi/api/org/schemas/organization/Organization';
import { UntilDestroy } from '@ngneat/until-destroy';

@UntilDestroy()
@Component({
  selector: 'pt-create-ai-assessment-dialog',
  templateUrl: './create-ai-assessment-dialog.component.html',
  styles: [
    `
      .form-readonly--custom {
        background: white !important;
      }

      .tree-selector {
        margin-bottom: 16px;
      }

      .tree-selector-clean {
        position: absolute;
        right: 7px;
        font-size: 7px !important;
        top: 8px;
      }

      .loader-container {
        height: 175px;
        display: grid;
        place-items: center;
      }
    `,
  ],
})
export class CreateAiAssessmentDialogComponent implements OnInit, OnDestroy {
  @Input() openOnSuccess = true;
  @Input() dialogMode: 'CREATE' | 'DUPLICATE' = 'CREATE';
  @Input() duplicateAiAssessmentPayload: DuplicateAiAssessmentPayload;
  @Input() selectedFolderId: number;
  @Input() selectedApplicationId: number;
  @ViewChild('popContentApplication') popContentApplication;

  public org$: Observable<Organization> = this.store.select(
    PtCoreSelectors.organization
  );
  private editInternalIdEnable = false;

  public folders: NavigationData[] = [];
  public applications: NavigationData[] = [];

  public selectedFolder: NavigationData;
  public selectedApplication: NavigationData;

  public formGroup: FormGroup;
  public REQUIRED = ValidatorGenerator.generateWidgetValidator(
    REQUIRED_VALIDATOR_KEY
  );

  private fields: string[] = [];
  public enabledTemplate = false;
  public loading = false;

  private destroy$: Subject<boolean> = new Subject<boolean>();

  constructor(
    public store: Store,
    public activeModal: NgbActiveModal,
    public actionsSubject: ActionsSubject,
    private folderService: FolderService,
    private aiAssessmentsService: AiAssessmentsService
  ) {}

  setLoading(value: boolean, withDelay = false) {
    if (!withDelay) {
      this.loading = value;
    } else {
      setTimeout(() => {
        this.loading = value;
      }, 300);
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
  }

  ngOnInit(): void {
    this.setLoading(true);
    this.initNavigationData();

    this.org$.pipe(takeUntil(this.destroy$)).subscribe((org) => {
      this.editInternalIdEnable = true; // org.editInternalIdEnable;
      this.alignDisabled();
    });

    this.actionsSubject
      .pipe(
        ofType(AiAssessmentActions.createAiAssessmentSuccess),
        takeUntil(this.destroy$)
      )
      .subscribe((action) => {
        if (this.openOnSuccess) {
          this.store.dispatch(
            PtCoreActions.navigateTo({
              route: [
                PtUiAppRoutes.BUSINESS_MANAGEMENT,
                PtUiAppRoutes.AI_ASSESSMENTS,
                PtUiAppRoutes.EDIT,
                action?.id.toString(),
              ],
            })
          );
        }
        this.activeModal.close(true);
      });

    this.actionsSubject
      .pipe(
        ofType(AiAssessmentActions.duplicateAiAssessmentSuccess),
        takeUntil(this.destroy$)
      )
      .subscribe((action) => {
        if (this.openOnSuccess) {
          this.store.dispatch(
            PtCoreActions.navigateTo({
              route: [
                PtUiAppRoutes.BUSINESS_MANAGEMENT,
                PtUiAppRoutes.AI_ASSESSMENTS,
                PtUiAppRoutes.EDIT,
                action.response?.id.toString(),
              ],
            })
          );
        }
        this.activeModal.close(true);
      });
  }

  private initNavigationData() {
    forkJoin({
      folders: this.folderService.findAllFolders(),
      applications: this.aiAssessmentsService.availableApp(),
    })
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (response: {
          folders: Folder[];
          applications: ListAppForNewAssessmentResponse;
        }) => {
          if (response.folders) {
            this.folders = response.folders.map((f) =>
              NavigationData.folderToNavigationData(f)
            );
          }

          if (response.applications) {
            this.applications =
              NavigationData.listAppForNewAssessmentResponseToNavigationData(
                response.applications
              );
          }

          this.initFormGroup();
        }
      );
  }

  private initFormGroup() {
    switch (this.dialogMode) {
      case 'CREATE':
        if (
          this.selectedFolderId !== null &&
          this.selectedFolderId !== undefined &&
          this.folders
        ) {
          this.selectedFolder = this.folders?.find(
            (f) => f.id == this.selectedFolderId
          );
        }

        if (
          this.selectedApplicationId !== null &&
          this.selectedApplicationId !== undefined &&
          this.applications
        ) {
          this.selectedApplication = this.applications?.find(
            (f) => f.id == this.selectedApplicationId
          );
        }

        this.formGroup = new FormGroup({
          name: new FormControl(null, [
            Validators.required,
            Validators.maxLength(128),
          ]),
          description: new FormControl(null),
          internalId: new FormControl(null),
          applicationId: new FormControl(this.selectedApplication?.name, [
            Validators.required,
          ]),
        });
        this.alignDisabled();
        break;
      case 'DUPLICATE':
        this.selectedFolder = this.folders?.find(
          (f) => f.id == this.duplicateAiAssessmentPayload?.payload?.parentId
        );
        this.selectedApplication = this.applications?.find(
          (a) =>
            a.id == this.duplicateAiAssessmentPayload?.payload?.applicationId
        );

        this.aiAssessmentsService
          .getToOverride()
          .pipe(takeUntil(this.destroy$))
          .subscribe((response) => {
            this.fields = Object.keys(response).filter(
              (r) => response[r] === true
            );

            this.formGroup = new FormGroup({
              name: new FormControl(null, [
                Validators.required,
                Validators.maxLength(128),
              ]),
              description: new FormControl(
                this.duplicateAiAssessmentPayload?.payload?.description
              ),
              internalId: new FormControl(null),
              applicationId: new FormControl(this.selectedApplication?.name, [
                Validators.required,
              ]),
            });
            this.alignDisabled();
          });
        break;
    }

    this.setLoading(false, true);
  }

  private alignDisabled() {
    if (this.formGroup) {
      if (this.editInternalIdEnable) {
        this.formGroup.controls.internalId.enable();
      } else {
        this.formGroup.controls.internalId.disable()
      }
    }
  }

  checkUsedField(field: string): boolean {
    switch (this.dialogMode) {
      case 'CREATE':
        return true;
      case 'DUPLICATE':
        return this.fields?.includes(field);
    }
  }

  confirm() {
    const response = {
      name: this.formGroup.controls.name.value,
      description: this.formGroup.controls.description.value,
      internalId: this.formGroup.controls.internalId.value,
      applicationId: this.selectedApplication?.id,
      parentId: this.selectedFolder?.id,
    } as AiAssessmentDocCreateRequest;

    switch (this.dialogMode) {
      case 'CREATE':
        this.store.dispatch(
          AiAssessmentActions.createAiAssessment({
            request: response,
          })
        );

        break;
      case 'DUPLICATE':
        this.store.dispatch(
          AiAssessmentActions.duplicateAiAssessment({
            id: this.duplicateAiAssessmentPayload?.payload?.docId,
            request: response,
          })
        );
        break;
    }
  }

  selectApplication(resource: NavigationData) {
    this.popContentApplication.close();
    this.selectedApplication = resource;
    this.formGroup.controls.applicationId.setValue(
      this.selectedApplication?.name
    );
  }

  chooseApplication() {
    this.popContentApplication.open();
  }

  cleanApplication() {
    this.formGroup?.controls?.applicationId?.setValue(null);
    this.selectedApplication = null;
  }
}
